// See https://github.com/sanity-io/demo-ecommerce/tree/main/apps/storefront/app/components/portableText

import type { PortableTextComponents } from '@portabletext/react';
import { PortableText as PortableTextReact } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import clsx from 'clsx';
import { useMemo } from 'react';
import dynamic from 'next/dynamic';

const LinkEmailAnnotation = dynamic(
  () => import('@/components/portableText/annotations/LinkEmail')
);
const LinkExternalAnnotation = dynamic(
  () => import('@/components/portableText/annotations/LinkExternal')
);
const LinkInternalAnnotation = dynamic(
  () => import('@/components/portableText/annotations/LinkInternal')
);
const AccordionBlock = dynamic(() => import('@/components/portableText/blocks/Accordion'));
const Block = dynamic(() => import('@/components/portableText/blocks/Block'));
const CalloutBlock = dynamic(() => import('@/components/portableText/blocks/Callout'));
const GridBlock = dynamic(() => import('@/components/portableText/blocks/Grid'));
const ImagesBlock = dynamic(() => import('@/components/portableText/blocks/Images'));
const InstagramBlock = dynamic(() => import('@/components/portableText/blocks/Instagram'));
// const ProductAnnotation = dynamic(() => import('@/components/portableText/annotations/Product'));
// const ProductsBlock = dynamic(() => import('@/components/portableText/blocks/Products'));
// const TaggedProductsBlock = dynamic(() => import('@/components/portableText/blocks/TaggedProducts'));

const SHARED_LIST_CLASSES = clsx(
  'first:mt-0 last:mb-0', //
  'my-8 space-y-0.5 leading-paragraph list-outside ml-8'
);

type Props = {
  blocks: PortableTextBlock[];
  className?: string;
  centered?: boolean;
};

export default function PortableText({ blocks, centered, className }: Props) {
  const components: PortableTextComponents = {
    list: {
      bullet: ({ children }) => <ul className={SHARED_LIST_CLASSES}>{children}</ul>,
      number: ({ children }) => <ol className={SHARED_LIST_CLASSES}>{children}</ol>
    },
    marks: {
      linkExternal: LinkExternalAnnotation,
      linkInternal: (props: any) => <LinkInternalAnnotation {...props} />,
      linkEmail: LinkEmailAnnotation
      // annotationProduct: (props: any) => <ProductAnnotation {...props} />
    },
    block: (props: any) => <Block {...props} />,
    types: {
      accordion: (props: any) => <AccordionBlock {...props} />,
      callout: (props: any) => <CalloutBlock centered={centered} {...props} />,
      grid: (props: any) => <GridBlock {...props} />,
      images: (props: any) => <ImagesBlock centered={centered} {...props} />,
      instagram: (props: any) => <InstagramBlock {...props} />
      // 'products': ProductsBlock,
      // 'taggedProducts': TaggedProductsBlock
    }
  };

  const portableText = useMemo(() => {
    return (
      <div className={clsx('portableText', className)}>
        <PortableTextReact value={blocks} components={components} />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  return portableText;
}
